import { defineStore } from 'pinia'
import { errorMessage } from '@/services/http'
import { jobsService } from '@/services/jobs'
import { applyBatch, summarize } from '@/utils/batch'
import { isRenderable } from '@/utils/files'
import { FILE_STATUS, JOB_STATUS } from '@/types/api'
import type { Job, RenderPayload, UserFile } from '@/types/api'

function blockedReason(file: UserFile): string {
  if (file.status === FILE_STATUS.failed) return 'this model could not be prepared'
  if (file.status === FILE_STATUS.pending) return 'this model is still being prepared'
  return 'this model must be converted to OBJ before it can be rendered'
}

function isBehind(incoming: Job, existing: Job): boolean {
  return incoming.status < existing.status
}

interface JobsState {
  items: Job[]
  loading: boolean
  submitting: boolean
  error: string | null
}

export const useJobsStore = defineStore('jobs', {
  state: (): JobsState => ({
    items: [],
    loading: false,
    submitting: false,
    error: null,
  }),

  getters: {
    active: (state): Job[] =>
      state.items.filter(
        (j) => j.status === JOB_STATUS.queued || j.status === JOB_STATUS.running,
      ),
  },

  actions: {
    async fetchQueue(): Promise<void> {
      if (this.loading) return
      this.loading = true
      this.error = null
      try {
        const snapshot = await jobsService.queue()
        const known = new Map(this.items.map((j) => [j.id, j]))
        this.items = snapshot.map((job) => {
          const existing = known.get(job.id)
          return existing && isBehind(job, existing) ? existing : job
        })
      } catch (e) {
        this.error = errorMessage(e)
        this.items = []
      } finally {
        this.loading = false
      }
    },

    applyJob(job: Job): void {
      const index = this.items.findIndex((j) => j.id === job.id)
      const previous = index === -1 ? undefined : this.items[index]
      if (previous && isBehind(job, previous)) return
      if (index === -1) this.items.unshift(job)
      else this.items[index] = job

    },

    async renderMany(
      files: UserFile[],
      payload: Omit<RenderPayload, 'file_ids'>,
    ): Promise<void> {
      if (!files.length) return

      const blocked = files.filter((file) => !isRenderable(file))
      if (blocked.length) {
        this.error = summarize(
          'render',
          0,
          blocked.map((file) => ({ filename: file.filename, reason: blockedReason(file) })),
        )
        return
      }

      await this.runBatch(async () => {
        try {
          const batch = await jobsService.render({
            file_ids: files.map((f) => f.id),
            ...payload,
          })
          applyBatch(
            'render',
            batch,
            (job) => this.applyJob(job),
            (message) => {
              this.error = message
            },
          )
        } catch (e) {
          this.error = errorMessage(e)
        }
      })
    },

    async runBatch(run: () => Promise<void>): Promise<void> {
      if (this.submitting) return
      this.submitting = true
      this.error = null
      try {
        await run()
      } finally {
        this.submitting = false
      }
    },
  },
})
